import { dateTime } from 'aos-helpers/src/helpers/Time'
import {
    InspectionItemPayload,
    InspectionTaskFormPayload,
} from 'aos-services/src/services/tasks/types/payload/InspectionTaskFormPayload'
import {
    InspectionGroup,
    translateInspectionGroup,
} from 'aos-services/src/services/tasks/types/task/InspectionGroup'
import {
    diffSelection,
    getLateDuration,
    InspectionTypeGroup,
    inspectionTypeToGroups,
} from 'aos-services/src/services/tasks/types/task/InspectionSelection'
import { translateInspectionType } from 'aos-services/src/services/tasks/types/task/InspectionType'
import { TaskCategory } from 'aos-services/src/services/tasks/types/TaskCategory'
import { allowedSubcategories } from 'aos-services/src/services/tasks/types/TaskConfig'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import {
    Box,
    EditableList,
    EditableListItemProps,
    HStack,
    Input,
    InputField,
    MultiSelect,
    Text,
} from 'aos-ui-universal'
import { EnumValues } from 'enum-values'
import type { TFunction } from 'i18next'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../TaskFormField'
import { InspectionTypeIconRenderer, LateInspectionLabel } from './partials'

interface InspectionTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const InspectionTaskForm: FC<InspectionTaskFormProps> = ({ metadata, isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext<InspectionTaskFormPayload>()
    const payload = useWatch({ control }) as InspectionTaskFormPayload
    const now = useMemo(() => dateTime(), [])
    const titleField = useController({ control, name: 'title' })

    const [inspectionGroup, setInspectionGroup] = useState<InspectionGroup>()
    const [inspectionTypeGroup, setInspectionTypeGroup] = useState<InspectionTypeGroup>(
        inspectionTypeToGroups(metadata.inspectionTypes, now, payload.items),
    )

    const groupConfigs = inspectionTypeToGroups(metadata.inspectionTypes, now, payload.items)
    const groups = Object.keys(groupConfigs).filter(q =>
        allowedSubcategories[TaskCategory.Inspection].includes(q),
    ) as InspectionGroup[]

    const onChangeSelection = (v: InspectionItemPayload[]) => {
        if (!inspectionGroup) {
            return
        }

        const nextItems = diffSelection(v, payload.items, inspectionGroup)
        const patch: Partial<InspectionTaskFormPayload> = { items: nextItems }
        if (v.length > 0) {
            patch.title = v[0].inspectionType.name
        } else {
            patch.title = ''
        }
        titleField.field.onChange(patch.title)

        setInspectionTypeGroup(prev => ({
            ...prev,
            [inspectionGroup]: {
                ...prev[inspectionGroup],
                selectedItems: v,
            },
        }))
    }

    const itemsById = useMemo(
        () =>
            new Map(
                EnumValues.getValues<InspectionGroup>(InspectionGroup)
                    .flatMap(g => inspectionTypeGroup[g]?.items ?? [])
                    .map(i => [String(i.inspectionType.id), i] as const),
            ),
        [inspectionTypeGroup],
    )

    const renderOption = useCallback(
        (option: { value: string; label: string }, isSelected: boolean) => {
            const item = itemsById.get(option.value)
            const lateLabel = item && getLateDuration(item, now)
            return (
                <HStack className='min-w-0 flex-1 items-center gap-3'>
                    <Text
                        size='caption'
                        tone={isSelected ? 'default' : 'muted'}
                        numberOfLines={1}
                        className='min-w-0 shrink'
                    >
                        {option.label}
                    </Text>
                    {lateLabel && <LateInspectionLabel duration={lateLabel} />}
                </HStack>
            )
        },
        [itemsById, now],
    )

    const onDelete = (items: InspectionItemPayload[]) => {
        setInspectionTypeGroup(prev => {
            const next: InspectionTypeGroup = { ...prev }
            const remainingIds = new Set(items.map(i => i.inspectionType.id))

            EnumValues.getValues<InspectionGroup>(InspectionGroup).forEach(groupName => {
                const group = next[groupName]
                if (!group) {
                    return
                }
                next[groupName] = {
                    ...group,
                    selectedItems: group.selectedItems.filter(si =>
                        remainingIds.has(si.inspectionType.id),
                    ),
                }
            })

            return next
        })
    }

    const hasGroupItems = !!inspectionGroup && !!inspectionTypeGroup[inspectionGroup]?.items.length

    return (
        <Box className='shrink-0 pb-3'>
            <Controller
                control={control}
                name='items'
                render={({ field, fieldState }) => (
                    <>
                        <TaskFormField
                            required={!isTaskTemplate}
                            error={
                                fieldState.error
                                    ? translate('form.required-field', { ns: 'common' })
                                    : undefined
                            }
                            label={translate('form.inspection.items.label', { ns: 'tasks' })}
                        >
                            <div className='grid grid-cols-4 gap-2'>
                                {groups.map(item => (
                                    <InspectionTypeIconRenderer
                                        key={item}
                                        isSelected={inspectionGroup === item}
                                        onSelect={() => setInspectionGroup(item)}
                                        group={item}
                                        isLate={inspectionTypeGroup[item].inspectionLate}
                                        selectedInspectionsCounter={
                                            inspectionTypeGroup[item].selectedItems.length
                                        }
                                        inspectionTotalNumber={
                                            inspectionTypeGroup[item].items.length
                                        }
                                    />
                                ))}
                            </div>
                        </TaskFormField>

                        {hasGroupItems && inspectionGroup && (
                            <MultiSelect
                                size='md'
                                options={inspectionTypeGroup[inspectionGroup].items.map(i => ({
                                    value: String(i.inspectionType.id),
                                    label: i.inspectionType.name,
                                }))}
                                value={inspectionTypeGroup[inspectionGroup].selectedItems.map(i =>
                                    String(i.inspectionType.id),
                                )}
                                summary={selected =>
                                    selected.length ===
                                    inspectionTypeGroup[inspectionGroup].items.length
                                        ? translate('all', {
                                              items: translateInspectionGroup(translate)(
                                                  inspectionGroup,
                                              ),
                                              ns: 'common',
                                          })
                                        : `Inspections: ${selected.length}`
                                }
                                renderOption={renderOption}
                                onChange={ids => {
                                    const next = ids
                                        .map(id => itemsById.get(id))
                                        .filter((i): i is InspectionItemPayload => !!i)
                                    onChangeSelection(next)
                                    field.onChange(next)
                                }}
                            />
                        )}

                        {!!field.value?.length && (
                            <Box className='shrink-0 pt-4'>
                                <EditableList
                                    value={field.value}
                                    itemRenderer={props => (
                                        <SelectedItemRenderer {...props} translate={translate} />
                                    )}
                                    onChange={items => {
                                        field.onChange(items)
                                        onDelete(items)
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            />
        </Box>
    )
}
type SelectedItemRendererProps = EditableListItemProps<InspectionItemPayload> & {
    translate: TFunction
}

export const SelectedItemRenderer: FC<SelectedItemRendererProps> = ({
    item,
    onChangeItem,
    translate,
}) => {
    return (
        <HStack className='items-center gap-5 pb-3 pr-5'>
            <Box className='flex-1'>
                <Text size='body' tone='muted' numberOfLines={1}>
                    {translateInspectionType(translate)(item.inspectionType)}
                </Text>
            </Box>
            <Box className='flex-1'>
                <Input>
                    <InputField
                        placeholder={translate('form.inspection.comment.placeholder', {
                            ns: 'tasks',
                        })}
                        value={item.comment ?? ''}
                        onChangeText={comment => onChangeItem({ ...item, comment })}
                    />
                </Input>
            </Box>
        </HStack>
    )
}
